/**
 * IA Console — SDR Conversation blocks API client (OpenSpec #2.5).
 *
 * Sobre o backend handler em /api/ia/sdr-conversation/blocks (GET/PUT).
 * Reusa o axios instance já configurado em apiService para herdar
 * baseURL + token interceptor.
 */
import axios from 'axios';
import { apiService } from './api';
import {
  SdrBlock,
  SdrConversationBlocksPack,
  SdrConversationVersion,
  SdrConversationVersionsList,
} from '../types';

interface AxiosLike {
  get<T>(url: string): Promise<{ data: T }>;
  put<T>(url: string, body: unknown): Promise<{ data: T }>;
  post<T>(url: string, body: unknown): Promise<{ data: T }>;
}

function getClient(): AxiosLike {
  // apiService keeps the configured axios instance private; we reach into
  // the same module-level construct used by the rest of the frontend. If
  // that ever changes, only this helper needs to be patched.
  const anySvc = apiService as unknown as { api: AxiosLike };
  return anySvc.api;
}

export async function getSdrConversationBlocks(): Promise<SdrConversationBlocksPack> {
  try {
    const res = await getClient().get<SdrConversationBlocksPack>('/ia/sdr-conversation/blocks');
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      throw new Error(err.response?.data?.message || 'Erro ao carregar blocos do Atendente IA');
    }
    throw err;
  }
}

export interface GuardrailsFailure {
  field: string;
  layer?: string;
  category?: string;
  reason?: string;
}

export interface PutBlocksError extends Error {
  field?: string;
  reason?: string;
  category?: string;
  status?: number;
  failures?: GuardrailsFailure[];
}

export async function putSdrConversationBlocks(
  blocks: SdrBlock[],
): Promise<SdrConversationBlocksPack> {
  try {
    const res = await getClient().put<SdrConversationBlocksPack>(
      '/ia/sdr-conversation/blocks',
      { blocks },
    );
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const data = err.response?.data || {};
      // Multi-block rejection from guardrails: o backend retorna
      // { error: 'guardrails_rejected', failures: [{field, layer, category, reason}] }.
      // Mapear a 1ª falha pro top-level pra UI conseguir destacar o bloco.
      const isGuardrails =
        data.error === 'guardrails_rejected' && Array.isArray(data.failures);
      const first: GuardrailsFailure | undefined = isGuardrails
        ? data.failures[0]
        : undefined;
      const message =
        first?.reason ||
        data.reason ||
        data.message ||
        'Erro ao salvar blocos do Atendente IA';
      const out = new Error(message) as PutBlocksError;
      out.field = first?.field ?? data.field;
      out.reason = first?.reason ?? data.reason;
      out.category = first?.category;
      out.status = err.response?.status;
      if (isGuardrails) out.failures = data.failures;
      throw out;
    }
    throw err;
  }
}

// OpenSpec #2.6 — real-time inline check (Layer A only, sub-100ms).
export interface RealtimeCheckResult {
  approved: boolean;
  layer: 'A' | 'B' | 'C' | 'all' | 'disabled';
  category?: string;
  reason?: string;
}

export async function checkBlockRealtime(input: string, field?: string): Promise<RealtimeCheckResult> {
  const res = await getClient().post<RealtimeCheckResult>(
    '/ia/guardrails/check?layer=A',
    { input, source: 'sdr_block', field },
  );
  return res.data;
}

// Histórico tab — list versions, fetch full snapshot, restore.
export async function listSdrConversationVersions(): Promise<SdrConversationVersionsList> {
  const res = await getClient().get<SdrConversationVersionsList>('/ia/sdr-conversation/versions');
  return res.data;
}

export async function getSdrConversationVersion(versionId: string): Promise<SdrConversationVersion> {
  const res = await getClient().get<SdrConversationVersion>(
    `/ia/sdr-conversation/versions/${encodeURIComponent(versionId)}`,
  );
  return res.data;
}

export interface RestoreResult {
  restoredFrom: { versionId: string; version: string };
  current: SdrConversationBlocksPack;
}

export async function restoreSdrConversationVersion(versionId: string): Promise<RestoreResult> {
  const res = await getClient().post<RestoreResult>(
    '/ia/sdr-conversation/versions/restore',
    { versionId },
  );
  return res.data;
}

// Playground — simulate conversation turn against the org's Atendente IA prompt.

export interface PlaygroundRunRequest {
  inboundText: string;
  conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  variables?: Record<string, string>;
  tier?: 'fast' | 'standard' | 'quality';
  /** Campos já capturados em turnos anteriores — Field Extractor evita re-extrair. */
  knownFields?: Record<string, unknown>;
}

export interface PlaygroundFieldExtraction {
  extracted: Record<string, string | number | boolean | null>;
  refusals: Array<{ key: string; reason?: string; at: string }>;
  confidence: number;
  providerId: string;
  modelId: string;
  latencyMs: number;
  inputTokens: number;
  outputTokens: number;
  parseFallback?: boolean;
}

export type HandoffSignal =
  | 'toxic'
  | 'opt_out'
  | 'low_intent'
  | 'request_human'
  | 'proposal_ready'
  | 'remarketing'
  | null;
export type FunnelStage = 'inicio' | 'em_negociacao' | 'coletando_dados';

export interface PlaygroundCrmManagerClassification {
  handoffSignal: HandoffSignal;
  handoffConfidence: number;
  funnelStage: FunnelStage;
  funnelConfidence: number;
  /** Meses até retomar contato — só presente quando handoffSignal === 'remarketing'. */
  remarketingMonths?: number;
  /** Lead pediu adiar mas falta(m) campo(s) essencial(is) — SDR coleta antes. */
  pendingFutureInterest?: boolean;
  reason?: string;
  providerId: string;
  modelId: string;
  latencyMs: number;
  inputTokens: number;
  outputTokens: number;
  parseFallback?: boolean;
}

export interface PlaygroundRunResult {
  text: string;
  modelId: string;
  providerId: string;
  inputTokens: number;
  outputTokens: number;
  latencyMs: number;
  costBilledUsd?: number;
  resolvedSystemPrompt: string;
  fallbackBaselineUsed?: boolean;
  /**
   * Resultado do Field Extractor agent quando FIELD_EXTRACTOR_ENABLED=true
   * (OpenSpec #2 — shadow mode). undefined quando flag desligada.
   */
  fieldExtraction?: PlaygroundFieldExtraction;
  /**
   * CRM Manager classifier — handoff signal + funnel stage — quando
   * CRM_MANAGER_CLASSIFIER_ENABLED=true. Shadow mode no Playground.
   */
  crmManagerClassification?: PlaygroundCrmManagerClassification;
}

export interface PlaygroundError {
  error: string;
  code?: string;
  message?: string;
  providerId?: string;
  retryable?: boolean;
}

export async function runPlaygroundTurn(req: PlaygroundRunRequest): Promise<PlaygroundRunResult> {
  const res = await getClient().post<PlaygroundRunResult>('/ia/playground/run', req);
  return res.data;
}
